import { useEffect, useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Megaphone, Plus, Trash2, Save, RefreshCw, ImagePlus, Eye, EyeOff,
  ArrowUp, ArrowDown, Link as LinkIcon, X, Sparkles,
} from 'lucide-react';

/**
 * Admin → Promo Banners (Iter 145++)
 * ====================================
 * Per-tenant CRUD for the auto-sliding promo carousel on the player home.
 *
 * Backend: `/api/admin/promo-banners` — scoped automatically by the admin's
 * tenant context (master admin → platform defaults, tenant admin → own).
 *
 * Features
 *  • Add / edit / delete banners
 *  • Drag-free reorder via ↑/↓ buttons (saves order to backend)
 *  • Toggle enabled per banner (eye icon)
 *  • Background-image upload per banner (PNG/JPG/WEBP, ≤8 MB)
 *  • Inline live preview on the right (mirrors the player carousel card)
 *  • Action picker: Referral / Deposit / External URL
 */

const ACCENT_PRESETS = [
  { name: 'Violet', accent: '#A78BFA', accent_deep: '#6D28D9', bg: 'linear-gradient(135deg, #2E1065 0%, #4C1D95 50%, #6D28D9 100%)' },
  { name: 'Crypto Gold', accent: '#F59E0B', accent_deep: '#B45309', bg: 'linear-gradient(135deg, #451A03 0%, #92400E 50%, #F59E0B 110%)' },
  { name: 'Hot Red', accent: '#F87171', accent_deep: '#B91C1C', bg: 'linear-gradient(135deg, #450A0A 0%, #991B1B 50%, #DC2626 100%)' },
  { name: 'Neon Green', accent: '#22FF88', accent_deep: '#065F46', bg: 'linear-gradient(135deg, #052E1A 0%, #064E3B 50%, #10B981 100%)' },
  { name: 'Sky Blue', accent: '#38BDF8', accent_deep: '#075985', bg: 'linear-gradient(135deg, #0C2E4D 0%, #0E4A6E 50%, #0EA5E9 100%)' },
  { name: 'Pink Magenta', accent: '#F472B6', accent_deep: '#9D174D', bg: 'linear-gradient(135deg, #3B0A24 0%, #831843 50%, #DB2777 100%)' },
];

const EMPTY_BANNER = {
  placement: 'home',
  title: '',
  highlight: '',
  eyebrow: '',
  body: '',
  cta: 'Open',
  action: 'url',
  url: '',
  redirect_enabled: true,
  accent: ACCENT_PRESETS[0].accent,
  accent_deep: ACCENT_PRESETS[0].accent_deep,
  bg_gradient: ACCENT_PRESETS[0].bg,
  enabled: true,
  image_only: false,
};

const PLACEMENT_META = {
  login: {
    label: 'Login / Landing Page',
    shortLabel: 'Login',
    description: 'Photo-only artwork shown on the public landing/login page before players sign in.',
    guide: { size: '1600 x 760 px', formats: 'PNG, JPG, WEBP, or GIF', max: '8 MB max' },
  },
  home: {
    label: 'Player Home Page',
    shortLabel: 'Home',
    description: 'Banners shown inside the player home screen above My Games.',
    guide: { size: '1600 x 640 px', formats: 'PNG, JPG, WEBP, or GIF', max: '8 MB max' },
  },
};

const PLACEMENT_ORDER = ['login', 'home'];

const normalizePlacement = (value) => (value === 'login' ? 'login' : 'home');
const placementMeta = (value) => PLACEMENT_META[normalizePlacement(value)] || PLACEMENT_META.home;

const PROMO_BANNER_IMAGE_GUIDE = {
  size: 'Login: 1600 x 760 px · Home: 1600 x 640 px',
  formats: 'PNG, JPG, WEBP, or GIF',
  max: '8 MB max',
};

export default function PromoBannersAdminPage() {
  const [banners, setBanners] = useState([]);
  const [tenantId, setTenantId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | banner object
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_BANNER });
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/admin/promo-banners');
      setBanners((r.data?.banners || []).map((b) => ({ ...b, placement: normalizePlacement(b.placement) })));
      setTenantId(r.data?.tenant_id ?? null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load promo banners');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // ─── Create / Edit ────────────────────────────────────────────────────
  const startCreate = (placement = 'home', imageOnly = false) => {
    const cleanPlacement = normalizePlacement(placement);
    setDraft({
      ...EMPTY_BANNER,
      placement: cleanPlacement,
      image_only: !!imageOnly,
      redirect_enabled: imageOnly ? false : EMPTY_BANNER.redirect_enabled,
      title: imageOnly ? `${placementMeta(cleanPlacement).shortLabel} photo banner` : '',
      cta: imageOnly ? 'Photo' : EMPTY_BANNER.cta,
    });
    setShowCreate(true);
    setEditing(null);
  };

  const startEdit = (b) => {
    setDraft({ ...b, placement: normalizePlacement(b.placement) });
    setShowCreate(true);
    setEditing(b);
  };

  const closeEditor = () => {
    setShowCreate(false);
    setEditing(null);
    setDraft({ ...EMPTY_BANNER });
  };

  const createPhotoBanner = async (placement) => {
    const cleanPlacement = normalizePlacement(placement);
    setSaving(true);
    try {
      const meta = placementMeta(cleanPlacement);
      await api.post('/api/admin/promo-banners', {
        ...EMPTY_BANNER,
        placement: cleanPlacement,
        title: `${meta.shortLabel} photo banner`,
        cta: 'Photo',
        image_only: true,
        redirect_enabled: false,
        enabled: true,
        accent: ACCENT_PRESETS[3].accent,
        accent_deep: ACCENT_PRESETS[3].accent_deep,
        bg_gradient: ACCENT_PRESETS[3].bg,
      });
      toast.success(`${meta.label} slot created. Upload its image now.`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create photo banner');
    } finally {
      setSaving(false);
    }
  };

  const validateDraft = () => {
    if (!draft.image_only && (!draft.title || draft.title.trim().length === 0)) {
      toast.error('Title is required');
      return false;
    }
    if (draft.action === 'url' && draft.url && !/^(\/|https?:\/\/)/i.test(draft.url)) {
      toast.error('URL must start with "/" or http(s)://');
      return false;
    }
    return true;
  };

  const submitDraft = async () => {
    if (!validateDraft()) return;
    setSaving(true);
    try {
      const payload = {
        ...draft,
        placement: normalizePlacement(draft.placement),
        title: draft.image_only
          ? (draft.title?.trim() || 'Photo banner')
          : draft.title,
        cta: draft.image_only
          ? (draft.cta?.trim() || 'Photo')
          : draft.cta,
      };
      if (editing && editing.id) {
        await api.put(`/api/admin/promo-banners/${editing.id}`, payload);
        toast.success('Banner updated');
      } else {
        await api.post('/api/admin/promo-banners', payload);
        toast.success('Banner created');
      }
      closeEditor();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save banner');
    } finally {
      setSaving(false);
    }
  };

  const removeBanner = async (b) => {
    if (!window.confirm(`Delete banner "${b.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/admin/promo-banners/${b.id}`);
      toast.success('Banner deleted');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete banner');
    }
  };

  const toggleEnabled = async (b) => {
    try {
      await api.put(`/api/admin/promo-banners/${b.id}`, { enabled: !b.enabled });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not toggle banner');
    }
  };

  // ─── Reorder ──────────────────────────────────────────────────────────
  const move = async (idx, delta) => {
    const next = [...banners];
    const tgt = idx + delta;
    if (tgt < 0 || tgt >= next.length) return;
    [next[idx], next[tgt]] = [next[tgt], next[idx]];
    setBanners(next);
    try {
      await api.post('/api/admin/promo-banners/reorder', { order: next.map((b) => b.id) });
    } catch (e) {
      toast.error('Reorder failed; reloading');
      await load();
    }
  };

  // ─── Image upload ─────────────────────────────────────────────────────
  const onSelectImage = async (b, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image too large (max 8 MB)');
      return;
    }
    setUploadingId(b.id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`/api/admin/promo-banners/${b.id}/image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Image uploaded');
      // Optimistic update
      setBanners((prev) => prev.map((x) => x.id === b.id ? { ...x, image_url: r.data?.image_url } : x));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Image upload failed');
    } finally {
      setUploadingId(null);
    }
  };

  const clearImage = async (b) => {
    if (!window.confirm('Remove background image from this banner?')) return;
    try {
      await api.delete(`/api/admin/promo-banners/${b.id}/image`);
      toast.success('Image removed');
      setBanners((prev) => prev.map((x) => x.id === b.id ? { ...x, image_url: null } : x));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove image');
    }
  };

  const resolveImage = (u) => {
    if (!u) return '';
    if (u.startsWith('http')) return u;
    return `${process.env.REACT_APP_BACKEND_URL || ''}${u}`;
  };

  const groupedBanners = PLACEMENT_ORDER.reduce((acc, key) => {
    acc[key] = banners.filter((b) => normalizePlacement(b.placement) === key);
    return acc;
  }, {});

  // ─── Render ───────────────────────────────────────────────────────────
  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="promo-banners-admin">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Megaphone className="w-6 h-6 text-amber-400" />
            <h1 className="text-2xl font-extrabold text-white">Promo Banners</h1>
          </div>
          <p className="text-sm text-white/60 max-w-2xl">
            {tenantId === null
              ? 'You are editing the platform-default banners. These are shown to any tenant that hasn\'t configured their own.'
              : 'These banners are shown to your players in the auto-sliding carousel on the home screen.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-white/80 text-sm transition"
            data-testid="promo-banners-refresh"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            onClick={() => startCreate('home')}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm transition active:scale-95"
            data-testid="promo-banners-add"
          >
            <Plus className="w-4 h-4" /> Add Banner
          </button>
        </div>
      </div>

      {!loading && (
        <div className="grid md:grid-cols-2 gap-4 mb-5" data-testid="promo-banner-placement-sections">
          {PLACEMENT_ORDER.map((placement) => {
            const meta = placementMeta(placement);
            const list = groupedBanners[placement] || [];
            const current = list.find((b) => b.image_only) || list[0];
            return (
              <div
                key={placement}
                className="rounded-2xl bg-[#121923] border border-amber-400/20 p-4"
                data-testid={`promo-banner-placement-${placement}`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <p className="text-white font-extrabold">{meta.label}</p>
                    <p className="text-xs text-white/55 mt-1">{meta.description}</p>
                  </div>
                  <span className="shrink-0 px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-[0.14em] bg-amber-400/10 text-amber-300 border border-amber-400/25">
                    {meta.shortLabel}
                  </span>
                </div>

                <div
                  className="rounded-2xl overflow-hidden relative mb-3"
                  style={{
                    aspectRatio: placement === 'login' ? '1600 / 760' : '1600 / 640',
                    background: current?.image_url
                      ? `url("${resolveImage(current.image_url)}") center/cover no-repeat`
                      : 'linear-gradient(135deg, rgba(34,255,136,0.12), rgba(245,215,110,0.08))',
                    border: '1px dashed rgba(245,215,110,0.35)',
                  }}
                >
                  {!current?.image_url && (
                    <div className="absolute inset-0 flex items-center justify-center text-center px-4">
                      <div>
                        <ImagePlus className="w-8 h-8 text-amber-300 mx-auto mb-2" />
                        <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/60">
                          Upload image only
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  {current ? (
                    <>
                      <label
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg cursor-pointer transition text-xs font-bold ${
                          uploadingId === current.id
                            ? 'bg-amber-500/30 text-amber-200'
                            : 'bg-amber-500 hover:bg-amber-400 text-black'
                        }`}
                        data-testid={`promo-banner-placement-upload-${placement}`}
                      >
                        {uploadingId === current.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                        {current.image_url ? 'Replace photo' : 'Upload photo'}
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/gif"
                          className="hidden"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            e.target.value = '';
                            onSelectImage(current, f);
                          }}
                        />
                      </label>
                      <button
                        onClick={() => startEdit(current)}
                        className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/80 text-xs font-bold"
                        data-testid={`promo-banner-placement-edit-${placement}`}
                      >
                        Edit slot
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => createPhotoBanner(placement)}
                      disabled={saving}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-xs font-bold disabled:opacity-60"
                      data-testid={`promo-banner-placement-create-${placement}`}
                    >
                      <Plus className="w-4 h-4" /> Create upload slot
                    </button>
                  )}
                  <span className="text-[11px] text-white/40">
                    Recommended: {meta.guide.size} · {meta.guide.formats} · {meta.guide.max}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="text-white/60 text-center py-20">Loading…</div>
      ) : banners.length === 0 ? (
        <div className="text-center py-20 rounded-2xl bg-white/5 border border-white/10">
          <Sparkles className="w-10 h-10 text-amber-400 mx-auto mb-3" />
          <p className="text-white/80 text-base font-bold mb-1">No banners yet</p>
          <p className="text-white/50 text-sm mb-4">Create your first promo banner to show on the player home.</p>
          <button
            onClick={() => startCreate('home')}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm"
            data-testid="promo-banners-empty-add"
          >
            <Plus className="w-4 h-4" /> Add Banner
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {banners.map((b, idx) => (
            <div
              key={b.id}
              className="rounded-2xl bg-[#131822] border border-white/10 p-4 flex flex-col lg:flex-row gap-4"
              data-testid={`promo-banner-row-${b.id}`}
            >
              {/* Preview card */}
              <div
                className="lg:w-80 shrink-0 rounded-2xl overflow-hidden relative"
                style={{
                  background: b.image_url
                    ? (b.image_only ? `url("${resolveImage(b.image_url)}") center/cover no-repeat` : `${b.bg_gradient}, url("${resolveImage(b.image_url)}") center/cover`)
                    : b.bg_gradient,
                  backgroundBlendMode: b.image_url && !b.image_only ? 'multiply' : 'normal',
                  border: `1.5px solid ${b.accent}66`,
                  boxShadow: `0 12px 28px rgba(0,0,0,0.45), 0 0 22px ${b.accent}55`,
                  minHeight: 130,
                }}
              >
                {b.image_only ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    {!b.image_url && (
                      <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/45">
                        Upload photo
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="px-4 py-3 relative">
                    <p className="text-[10px] uppercase tracking-[0.2em] font-bold mb-1" style={{ color: b.accent, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                      {b.eyebrow || 'Promo'}
                    </p>
                    <p className="font-extrabold leading-tight text-white text-base" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.55)' }}>
                      {b.title}
                      {b.highlight ? (
                        <>
                          {' · '}
                          <span style={{ color: b.accent, textShadow: `0 0 14px ${b.accent}88` }}>{b.highlight}</span>
                        </>
                      ) : null}
                    </p>
                    <p className="text-xs mt-1 leading-snug line-clamp-2" style={{ color: 'rgba(255,255,255,0.85)', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                      {b.body}
                    </p>
                    <div className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold" style={{ background: 'rgba(0,0,0,0.45)', border: `1px solid ${b.accent}66`, color: '#fff' }}>
                      {b.cta}
                    </div>
                  </div>
                )}
              </div>

              {/* Meta + actions */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wider" style={{ background: b.enabled ? '#10B98122' : '#71717A22', color: b.enabled ? '#10B981' : '#A1A1AA', border: `1px solid ${b.enabled ? '#10B98155' : '#71717A55'}` }}>
                    {b.enabled ? 'Active' : 'Hidden'}
                  </span>
                  <span className="px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wider bg-amber-400/10 text-amber-300 border border-amber-400/25">
                    {placementMeta(b.placement).shortLabel}
                  </span>
                  <span className="px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wider bg-white/5 text-white/60 border border-white/10">
                    {b.redirect_enabled === false ? 'No redirect' : b.action}
                  </span>
                  {b.action === 'url' && b.url && (
                    <span className="text-[11px] text-white/50 truncate max-w-[260px] flex items-center gap-1">
                      <LinkIcon className="w-3 h-3" /> {b.url}
                    </span>
                  )}
                </div>
                <p className="text-white font-bold mb-1 truncate">{b.image_only ? (b.title || 'Photo banner') : b.title}</p>
                <p className="text-white/60 text-sm mb-3 line-clamp-2">
                  {b.image_only
                    ? (b.redirect_enabled === false ? 'Photo only · redirect off' : 'Photo only · redirects on tap')
                    : (b.body || '—')}
                </p>

                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/80 disabled:opacity-30 transition"
                    title="Move up"
                    data-testid={`promo-banner-up-${b.id}`}
                  ><ArrowUp className="w-4 h-4" /></button>
                  <button
                    onClick={() => move(idx, +1)}
                    disabled={idx === banners.length - 1}
                    className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/80 disabled:opacity-30 transition"
                    title="Move down"
                    data-testid={`promo-banner-down-${b.id}`}
                  ><ArrowDown className="w-4 h-4" /></button>
                  <button
                    onClick={() => toggleEnabled(b)}
                    className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/80 transition"
                    title={b.enabled ? 'Hide banner' : 'Show banner'}
                    data-testid={`promo-banner-toggle-${b.id}`}
                  >
                    {b.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <label
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg cursor-pointer transition text-xs font-bold ${
                      uploadingId === b.id
                        ? 'bg-amber-500/30 text-amber-200'
                        : 'bg-white/5 hover:bg-white/10 text-white/80'
                    }`}
                    data-testid={`promo-banner-upload-${b.id}`}
                  >
                    {uploadingId === b.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                    {b.image_url ? 'Replace image' : 'Upload image'}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = '';
                        onSelectImage(b, f);
                      }}
                    />
                  </label>
                  <span className="text-[11px] text-white/40">
                    Recommended: {placementMeta(b.placement).guide.size} · {placementMeta(b.placement).guide.formats} · {placementMeta(b.placement).guide.max}
                  </span>
                  {b.image_url && (
                    <button
                      onClick={() => clearImage(b)}
                      className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition"
                      title="Remove image"
                      data-testid={`promo-banner-clear-image-${b.id}`}
                    ><Trash2 className="w-4 h-4" /></button>
                  )}
                  <button
                    onClick={() => startEdit(b)}
                    className="px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs transition active:scale-95"
                    data-testid={`promo-banner-edit-${b.id}`}
                  >Edit</button>
                  <button
                    onClick={() => removeBanner(b)}
                    className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition"
                    title="Delete"
                    data-testid={`promo-banner-delete-${b.id}`}
                  ><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit modal */}
      {showCreate && (
        <BannerEditor
          draft={draft}
          setDraft={setDraft}
          onClose={closeEditor}
          onSubmit={submitDraft}
          saving={saving}
          isEdit={!!editing}
        />
      )}
    </div>
  );
}

// ─────────────────────────── Banner editor modal ───────────────────────────
function BannerEditor({ draft, setDraft, onClose, onSubmit, saving, isEdit }) {
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const meta = placementMeta(draft.placement);
  const resolveEditorImage = (u) => {
    if (!u) return '';
    if (u.startsWith('http')) return u;
    return `${process.env.REACT_APP_BACKEND_URL || ''}${u}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur" data-testid="promo-banner-editor">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-[#0F1218] border border-white/10 shadow-2xl">
        <div className="sticky top-0 flex items-center justify-between gap-3 px-5 py-4 bg-[#0F1218]/95 backdrop-blur border-b border-white/10">
          <div>
            <p className="text-white font-extrabold text-lg">{isEdit ? 'Edit promo banner' : 'New promo banner'}</p>
            <p className="text-white/50 text-xs">Choose whether this artwork appears on the login page or player home.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/80" data-testid="promo-banner-editor-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded-2xl border border-amber-400/20 bg-amber-500/5 px-4 py-3">
            <p className="text-white font-bold text-sm">Banner image guide</p>
            <p className="text-white/65 text-xs mt-1">
              Recommended image size for <span className="font-semibold text-amber-300">{meta.label}</span>: <span className="font-semibold text-amber-300">{meta.guide.size}</span>.
              Use {meta.guide.formats}. {meta.guide.max}.
            </p>
            <p className="text-white/50 text-xs mt-1">
              For a clean artwork-only slide, switch to <span className="text-white/80 font-semibold">Photo only</span>, turn redirect off if needed, save the banner, then upload the image from the banner card list.
            </p>
          </div>

          <div>
            <p className="text-xs uppercase tracking-wider text-white/60 mb-1.5 font-bold">Where to show</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {PLACEMENT_ORDER.map((placement) => {
                const pMeta = placementMeta(placement);
                const active = normalizePlacement(draft.placement) === placement;
                return (
                  <button
                    key={placement}
                    type="button"
                    onClick={() => set('placement', placement)}
                    className={`text-left rounded-xl px-3 py-2 border transition ${
                      active
                        ? 'bg-amber-500 text-black border-amber-300'
                        : 'bg-white/[0.03] hover:bg-white/[0.06] text-white/80 border-white/10'
                    }`}
                    data-testid={`promo-banner-placement-option-${placement}`}
                  >
                    <span className="block text-sm font-extrabold">{pMeta.label}</span>
                    <span className={`block text-[11px] mt-0.5 ${active ? 'text-black/70' : 'text-white/45'}`}>{pMeta.guide.size}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-wider text-white/60 mb-1.5 font-bold">Banner style</p>
            <div className="flex flex-wrap gap-2">
              {[
                { key: false, label: 'Text + image banner' },
                { key: true, label: 'Photo only banner' },
              ].map((opt) => (
                <button
                  key={String(opt.key)}
                  onClick={() => set('image_only', opt.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                    !!draft.image_only === opt.key
                      ? 'bg-amber-500 text-black'
                      : 'bg-white/5 hover:bg-white/10 text-white/80'
                  }`}
                  data-testid={`promo-banner-style-${opt.key ? 'photo-only' : 'text-image'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Live preview */}
          <div
            className="rounded-2xl overflow-hidden relative"
            style={{
              background: draft.image_url
                ? (draft.image_only ? `url("${resolveEditorImage(draft.image_url)}") center/cover no-repeat` : `${draft.bg_gradient}, url("${resolveEditorImage(draft.image_url)}") center/cover`)
                : draft.bg_gradient,
              backgroundBlendMode: draft.image_url && !draft.image_only ? 'multiply' : 'normal',
              border: `1.5px solid ${draft.accent}66`,
              boxShadow: `0 12px 28px rgba(0,0,0,0.45), 0 0 22px ${draft.accent}55`,
              minHeight: draft.placement === 'login' ? 160 : 130,
              aspectRatio: draft.image_only ? (draft.placement === 'login' ? '1600 / 760' : '1600 / 640') : undefined,
            }}
          >
            {draft.image_only ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/35">
                  Uploaded photo only
                </p>
              </div>
            ) : (
              <div className="px-4 py-4">
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold mb-1" style={{ color: draft.accent }}>
                  {draft.eyebrow || 'Eyebrow'}
                </p>
                <p className="font-extrabold leading-tight text-white text-base">
                  {draft.title || 'Banner title'}
                  {draft.highlight ? <> · <span style={{ color: draft.accent }}>{draft.highlight}</span></> : null}
                </p>
                <p className="text-xs mt-1 text-white/85 line-clamp-2">{draft.body || 'Subtitle / body line'}</p>
                <div className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold" style={{ background: 'rgba(0,0,0,0.45)', border: `1px solid ${draft.accent}66`, color: '#fff' }}>
                  {draft.cta || 'Open'}
                </div>
              </div>
            )}
          </div>

          {/* Text fields */}
          {draft.image_only ? (
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Internal name (optional)">
                <input value={draft.title} onChange={(e) => set('title', e.target.value)}
                  placeholder="Summer promo artwork"
                  maxLength={80}
                  className="w-full px-3 py-2 rounded-lg bg-[#131822] border border-white/10 text-white text-sm focus:outline-none focus:border-amber-400/60"
                  data-testid="promo-banner-input-title" />
              </Field>
              <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-white/55">
                Photo-only banners show only the uploaded image on the player home. No title, no CTA, no overlay text.
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Eyebrow (small caption)">
                <input value={draft.eyebrow} onChange={(e) => set('eyebrow', e.target.value)}
                  placeholder="Refer & Earn"
                  maxLength={40}
                  className="w-full px-3 py-2 rounded-lg bg-[#131822] border border-white/10 text-white text-sm focus:outline-none focus:border-amber-400/60"
                  data-testid="promo-banner-input-eyebrow" />
              </Field>
              <Field label="CTA (button label)">
                <input value={draft.cta} onChange={(e) => set('cta', e.target.value)}
                  placeholder="Invite & earn"
                  maxLength={30}
                  className="w-full px-3 py-2 rounded-lg bg-[#131822] border border-white/10 text-white text-sm focus:outline-none focus:border-amber-400/60"
                  data-testid="promo-banner-input-cta" />
              </Field>
              <Field label="Title *">
                <input value={draft.title} onChange={(e) => set('title', e.target.value)}
                  placeholder="Invite a friend"
                  maxLength={80}
                  className="w-full px-3 py-2 rounded-lg bg-[#131822] border border-white/10 text-white text-sm focus:outline-none focus:border-amber-400/60"
                  data-testid="promo-banner-input-title" />
              </Field>
              <Field label="Highlight (accent text)">
                <input value={draft.highlight} onChange={(e) => set('highlight', e.target.value)}
                  placeholder="Get $5 FP"
                  maxLength={60}
                  className="w-full px-3 py-2 rounded-lg bg-[#131822] border border-white/10 text-white text-sm focus:outline-none focus:border-amber-400/60"
                  data-testid="promo-banner-input-highlight" />
              </Field>
              <Field label="Body (2-line subtext)" colSpan>
                <input value={draft.body} onChange={(e) => set('body', e.target.value)}
                  placeholder="When they sign up & make their 1st deposit"
                  maxLength={200}
                  className="w-full px-3 py-2 rounded-lg bg-[#131822] border border-white/10 text-white text-sm focus:outline-none focus:border-amber-400/60"
                  data-testid="promo-banner-input-body" />
              </Field>
            </div>
          )}

          {/* Action picker */}
          <div>
            <div className="flex items-center justify-between gap-3 mb-2">
              <div>
                <p className="text-xs uppercase tracking-wider text-white/60 font-bold">Tap action</p>
                <p className="text-[11px] text-white/40">
                  Turn redirect off when the banner should be artwork only.
                </p>
              </div>
              <button
                type="button"
                onClick={() => set('redirect_enabled', draft.redirect_enabled === false)}
                className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                  draft.redirect_enabled === false
                    ? 'bg-white/5 hover:bg-white/10 text-white/70'
                    : 'bg-emerald-500 text-black'
                }`}
                data-testid="promo-banner-redirect-toggle"
              >
                {draft.redirect_enabled === false ? 'Redirect off' : 'Redirect on'}
              </button>
            </div>
            {draft.redirect_enabled === false ? (
              <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-white/55">
                Redirect is off. Players can swipe past this banner, but tapping it will not open anything.
              </div>
            ) : (
            <div className="flex flex-wrap gap-2 mb-2">
              {[
                { key: 'referral', label: 'Open referral page' },
                { key: 'deposit', label: 'Open deposit flow' },
                { key: 'url', label: 'Custom URL / route' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => set('action', opt.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                    draft.action === opt.key
                      ? 'bg-amber-500 text-black'
                      : 'bg-white/5 hover:bg-white/10 text-white/80'
                  }`}
                  data-testid={`promo-banner-action-${opt.key}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            )}
            {draft.redirect_enabled !== false && draft.action === 'url' && (
              <input
                value={draft.url}
                onChange={(e) => set('url', e.target.value)}
                placeholder="/app/explore?game=juwa  or  https://example.com"
                className="w-full px-3 py-2 rounded-lg bg-[#131822] border border-white/10 text-white text-sm focus:outline-none focus:border-amber-400/60"
                data-testid="promo-banner-input-url"
              />
            )}
          </div>

          {/* Color presets + custom */}
          <div>
            <p className="text-xs uppercase tracking-wider text-white/60 mb-1.5 font-bold">Color preset</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {ACCENT_PRESETS.map((p) => (
                <button
                  key={p.name}
                  onClick={() => setDraft((d) => ({ ...d, accent: p.accent, accent_deep: p.accent_deep, bg_gradient: p.bg }))}
                  className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold text-white border transition active:scale-95"
                  style={{
                    background: p.bg,
                    borderColor: draft.accent === p.accent ? p.accent : 'rgba(255,255,255,0.1)',
                    boxShadow: draft.accent === p.accent ? `0 0 0 2px ${p.accent}88` : 'none',
                  }}
                  data-testid={`promo-banner-preset-${p.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {p.name}
                </button>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Accent color">
                <div className="flex items-center gap-2">
                  <input type="color" value={draft.accent} onChange={(e) => set('accent', e.target.value)} className="w-12 h-9 rounded-md cursor-pointer bg-transparent border border-white/10" />
                  <input value={draft.accent} onChange={(e) => set('accent', e.target.value)} className="flex-1 px-3 py-2 rounded-lg bg-[#131822] border border-white/10 text-white text-sm font-mono" />
                </div>
              </Field>
              <Field label="Deep accent">
                <div className="flex items-center gap-2">
                  <input type="color" value={draft.accent_deep} onChange={(e) => set('accent_deep', e.target.value)} className="w-12 h-9 rounded-md cursor-pointer bg-transparent border border-white/10" />
                  <input value={draft.accent_deep} onChange={(e) => set('accent_deep', e.target.value)} className="flex-1 px-3 py-2 rounded-lg bg-[#131822] border border-white/10 text-white text-sm font-mono" />
                </div>
              </Field>
              <Field label="Background gradient (CSS)" colSpan>
                <textarea
                  value={draft.bg_gradient}
                  onChange={(e) => set('bg_gradient', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg bg-[#131822] border border-white/10 text-white text-xs font-mono focus:outline-none focus:border-amber-400/60"
                  data-testid="promo-banner-input-gradient"
                />
              </Field>
            </div>
          </div>

          {/* Enabled */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={draft.enabled} onChange={(e) => set('enabled', e.target.checked)} className="w-4 h-4 accent-amber-500" data-testid="promo-banner-input-enabled" />
            <span className="text-white/80 text-sm">Show this banner on the {meta.label.toLowerCase()}</span>
          </label>
        </div>

        <div className="sticky bottom-0 flex items-center justify-end gap-2 px-5 py-3 bg-[#0F1218]/95 backdrop-blur border-t border-white/10">
          <button onClick={onClose} disabled={saving}
            className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-white/80 text-sm font-bold disabled:opacity-50"
            data-testid="promo-banner-editor-cancel"
          >Cancel</button>
          <button onClick={onSubmit} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm transition active:scale-95 disabled:opacity-60"
            data-testid="promo-banner-editor-save"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isEdit ? 'Update banner' : 'Create banner'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, colSpan }) {
  return (
    <div className={colSpan ? 'sm:col-span-2' : ''}>
      <label className="block text-[11px] uppercase tracking-wider text-white/60 mb-1 font-bold">{label}</label>
      {children}
    </div>
  );
}
